import React, {PureComponent} from 'react';
import {Card, CardContent, Typography} from '@mui/material';
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import CardActionArea from "@mui/material/CardActionArea";
import {withRouter} from "../../../hooks/withRouter";
import Collapse from "@mui/material/Collapse";
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import {Line} from "react-chartjs-2";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import moment from "moment";
import BarChartIcon from '@mui/icons-material/BarChart';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import {UserContext} from "../../../core/UserContext";
import {ColorPicker} from "../../../core/colors/ColorPicker";

class GeneralStatsCard extends PureComponent {
	static contextType = UserContext;

	constructor(props) {
		super(props);
		this.state = {
			statistics: UserLocalStorage.get(UrlBuilder.user.statisticsApi()) ?? null,
			isCollapseOpen: false,
			isLoading: false,
			hasLoadedAtLeastOnce: false,
			muscleBreakdownMode: 'sets',
		};
	}

	hasNeverBeenFetched = () => {
		return this.state.statistics === null;
	}

	fetch = async () => {
		this.setState({ isLoading: true });

		const now = (moment()).format('L');
		const response = await fetch(UrlBuilder.user.statisticsApi(now));

		if (response.status !== 200) {
			this.setState({ isLoading: false });
			return;
		}

		const responseData = await response.json();

		this.setState({
			statistics: responseData['data'] ?? [],
			isLoading: false,
			hasLoadedAtLeastOnce: true,
		});

		UserLocalStorage.set(UrlBuilder.user.statisticsApi(), responseData['data'] ?? []);
	}

	triggerCollapse = () => {
		this.setState({ isCollapseOpen: !this.state.isCollapseOpen });

		if (!this.state.isCollapseOpen && !this.state.hasLoadedAtLeastOnce) {
			this.fetch();
		}
	}

	getChartData = () => {
		const labels = this.state.statistics.lastTwelveMonthsWorkoutCount.map(item => `${item.month}/${item.year}`);
		const data = this.state.statistics.lastTwelveMonthsWorkoutCount.map(item => item.workoutCount);

		return {
			labels,
			datasets: [
				{
					label: 'Workouts',
					data,
					fill: true,
					backgroundColor: 'rgba(245, 0, 87, 0.08)',
					borderColor: 'rgba(245, 0, 87, 0.6)',
					borderWidth: 2,
					pointBackgroundColor: 'rgba(245, 0, 87, 0.8)',
					pointRadius: 3,
					pointHoverRadius: 5,
					tension: 0.3,
				},
			],
		};
	}

	getChartOptions = () => ({
		legend: { display: false },
		scales: {
			yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
			xAxes: [{ ticks: { fontSize: 10 } }],
		},
		tooltips: {
			callbacks: {
				label: (item) => `${item.yLabel} workout${item.yLabel !== 1 ? 's' : ''}`,
			},
		},
	})

	renderWorkoutTime = (workoutTime) => {
		if (!workoutTime) return '—';
		const [hours, minutes] = workoutTime.split(':');
		return `${parseInt(hours)}h ${minutes}m`;
	}

	formatVolume = (volume) => {
		if (volume >= 1000000) return (volume / 1000000).toFixed(1) + 'M';
		if (volume >= 1000) return (volume / 1000).toFixed(1) + 'K';
		return Math.round(volume).toString();
	}

	getUnitLabel = () => {
		const { user } = this.context;
		return user?.preferredUnitSystemId === 2 ? 'lbs' : 'kg';
	}

	renderStatRow = (label, value, detail) => (
		<Box sx={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			py: 1,
		}}>
			<Box>
				<Typography variant="body2" sx={{ fontWeight: 500 }}>
					{label}
				</Typography>
				{detail && (
					<Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
						{detail}
					</Typography>
				)}
			</Box>
			<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
				{value}
			</Typography>
		</Box>
	)

	renderStatCard = (value, label) => (
		<Paper variant="outlined" sx={{
			borderRadius: 2,
			p: 1.5,
			textAlign: 'center',
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
		}}>
			<Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
				{value}
			</Typography>
			<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
				{label}
			</Typography>
		</Paper>
	)

	renderSectionHeader = (icon, label) => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
			{React.cloneElement(icon, { sx: { fontSize: 16, color: 'text.disabled' } })}
			<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
				{label}
			</Typography>
		</Box>
	)

	renderTintedSection = (children) => (
		<Box sx={{
			bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
			borderRadius: 2,
			px: 2,
			mb: 2,
		}}>
			{children}
		</Box>
	)

	renderTopExercises = (topExercises) => {
		if (!topExercises || topExercises.length === 0) return null;

		return (
			<Box sx={{ mb: 2 }}>
				{this.renderSectionHeader(<EmojiEventsIcon />, 'Top Exercises')}
				{topExercises.map((exercise, index) => (
					<Box key={index} sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1.5,
						py: 1,
						borderBottom: index < topExercises.length - 1 ? 1 : 0,
						borderColor: 'divider',
					}}>
						<Typography variant="body2" sx={{ fontWeight: 700, color: 'text.disabled', width: 18, textAlign: 'center' }}>
							{index + 1}
						</Typography>
						<Avatar sx={{
							width: 32,
							height: 32,
							bgcolor: exercise.color ?? ColorPicker.pick(exercise.name),
							fontSize: 14,
							fontWeight: 700,
						}}>
							{exercise.name?.charAt(0)?.toUpperCase()}
						</Avatar>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
								{exercise.name}
							</Typography>
							<Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
								{exercise.sessionCount} session{exercise.sessionCount !== 1 ? 's' : ''} · {exercise.setCount} set{exercise.setCount !== 1 ? 's' : ''}
							</Typography>
						</Box>
					</Box>
				))}
			</Box>
		);
	}

	getMuscleColor = (index, total) => {
		if (index === 0) return 'secondary.main';
		const opacity = 1 - (index / total) * 0.65;
		return `rgba(245, 0, 87, ${opacity})`;
	}

	renderMuscleBreakdown = (muscleBreakdown) => {
		if (!muscleBreakdown || muscleBreakdown.length === 0) return null;

		const mode = this.state.muscleBreakdownMode;
		const hasVolumeData = muscleBreakdown.some(m => m.volume > 0);
		const unitLabel = this.getUnitLabel();

		const sorted = mode === 'volume' && hasVolumeData
			? [...muscleBreakdown].sort((a, b) => b.volume - a.volume)
			: muscleBreakdown;

		return (
			<Box sx={{ mb: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
					{this.renderSectionHeader(<AccessibilityNewIcon />, 'Muscle Breakdown')}
					{hasVolumeData &&
						<Box sx={{ display: 'flex', gap: 0.5 }}>
							<Chip
								label="Sets"
								size="small"
								variant={mode === 'sets' ? 'filled' : 'outlined'}
								color={mode === 'sets' ? 'secondary' : 'default'}
								onClick={() => this.setState({ muscleBreakdownMode: 'sets' })}
								sx={{ height: 22, fontSize: 11 }}
							/>
							<Chip
								label="Volume"
								size="small"
								variant={mode === 'volume' ? 'filled' : 'outlined'}
								color={mode === 'volume' ? 'secondary' : 'default'}
								onClick={() => this.setState({ muscleBreakdownMode: 'volume' })}
								sx={{ height: 22, fontSize: 11 }}
							/>
						</Box>
					}
				</Box>
				{sorted.map((muscle, index) => {
					const barPercent = mode === 'volume' ? muscle.volumePercentage : muscle.percentage;
					const label = mode === 'volume'
						? `${this.formatVolume(muscle.volume)} ${unitLabel}`
						: `${muscle.setCount} set${muscle.setCount !== 1 ? 's' : ''}`;

					return (
						<Box key={muscle.name} sx={{ mb: index < sorted.length - 1 ? 1.25 : 0 }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
								<Typography variant="body2" sx={{ fontWeight: index === 0 ? 700 : 500, fontSize: 13 }}>
									{muscle.name}
								</Typography>
								<Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
									{label}
								</Typography>
							</Box>
							<Box sx={{
								height: 8,
								borderRadius: 4,
								bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
								overflow: 'hidden',
							}}>
								<Box sx={{
									height: '100%',
									width: `${barPercent}%`,
									borderRadius: 4,
									bgcolor: this.getMuscleColor(index, sorted.length),
									transition: 'width 0.5s ease',
								}} />
							</Box>
						</Box>
					);
				})}
			</Box>
		);
	}

	renderCardContent = (statistics) => {
		const unitLabel = this.getUnitLabel();
		const hasVolume = statistics.totalVolume > 0;

		return (
			<Box>
				{/* Total Workouts */}
				{this.renderSectionHeader(<FitnessCenterIcon />, 'Total Workouts')}
				<Grid container spacing={1} sx={{ mb: 2 }}>
					<Grid item xs={4}>
						{this.renderStatCard(statistics.totalWorkoutCount, 'All Time')}
					</Grid>
					<Grid item xs={4}>
						{this.renderStatCard(statistics.workoutCountThisYear, 'This Year')}
					</Grid>
					<Grid item xs={4}>
						{this.renderStatCard(statistics.workoutCountThisMonth, 'This Month')}
					</Grid>
				</Grid>

				{/* Chart */}
				<Box sx={{ mb: 2 }}>
					<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11, display: 'block', mb: 1 }}>
						Workouts Per Month
					</Typography>
					<Line data={this.getChartData()} options={this.getChartOptions()} />
				</Box>

				{/* Total Volume */}
				{hasVolume && <>
					{this.renderSectionHeader(<FitnessCenterIcon />, `Total Volume (${unitLabel})`)}
					<Grid container spacing={1} sx={{ mb: 2 }}>
						<Grid item xs={4}>
							{this.renderStatCard(this.formatVolume(statistics.totalVolume), 'All Time')}
						</Grid>
						<Grid item xs={4}>
							{this.renderStatCard(this.formatVolume(statistics.volumeThisYear), 'This Year')}
						</Grid>
						<Grid item xs={4}>
							{this.renderStatCard(this.formatVolume(statistics.volumeThisMonth), 'This Month')}
						</Grid>
					</Grid>
				</>}

				{/* Total Sets */}
				{statistics.totalSets > 0 &&
					<>
						{this.renderSectionHeader(<FitnessCenterIcon />, 'Total Sets')}
						<Grid container spacing={1} sx={{ mb: 2 }}>
							<Grid item xs={4}>
								{this.renderStatCard(statistics.totalSets.toLocaleString(), 'All Time')}
							</Grid>
							<Grid item xs={4}>
								{this.renderStatCard(statistics.setsThisYear.toLocaleString(), 'This Year')}
							</Grid>
							<Grid item xs={4}>
								{this.renderStatCard(statistics.setsThisMonth.toLocaleString(), 'This Month')}
							</Grid>
						</Grid>
					</>
				}

				{/* Frequency */}
				{this.renderTintedSection(<>
					<Box sx={{ pt: 1.5, pb: 0.5 }}>
						{this.renderSectionHeader(<FitnessCenterIcon />, 'Frequency')}
					</Box>
					{this.renderStatRow('Per week', statistics.averageWorkoutCountPerWeek.toFixed(1), 'Average')}
					<Divider />
					{this.renderStatRow('Per month', statistics.averageWorkoutCountPerMonth.toFixed(1), 'Average')}
				</>)}

				{/* Duration */}
				{this.renderTintedSection(<>
					<Box sx={{ pt: 1.5, pb: 0.5 }}>
						{this.renderSectionHeader(<TimerIcon />, 'Duration')}
					</Box>
					{this.renderStatRow('Average', this.renderWorkoutTime(statistics.averageWorkoutTime))}
					<Divider />
					{this.renderStatRow('Avg. this month', this.renderWorkoutTime(statistics.averageWorkoutTimeThisMonth))}
					<Divider />
					{this.renderStatRow('Avg. this year', this.renderWorkoutTime(statistics.averageWorkoutTimeThisYear))}
					{statistics.longestWorkoutDuration && <>
						<Divider />
						{this.renderStatRow('Longest', this.renderWorkoutTime(statistics.longestWorkoutDuration))}
					</>}
					{statistics.shortestWorkoutDuration && <>
						<Divider />
						{this.renderStatRow('Shortest', this.renderWorkoutTime(statistics.shortestWorkoutDuration))}
					</>}
				</>)}

				{/* Training Profile */}
				{statistics.preferredTrainingTime &&
					this.renderTintedSection(<>
						<Box sx={{ pt: 1.5, pb: 0.5 }}>
							{this.renderSectionHeader(<AccessTimeIcon />, 'Training Profile')}
						</Box>
						{this.renderStatRow('Preferred time', statistics.preferredTrainingTime)}
					</>)
				}

				{/* Top 5 Exercises */}
				{this.renderTopExercises(statistics.topExercises)}

				{/* Muscle Breakdown */}
				{this.renderMuscleBreakdown(statistics.muscleBreakdown)}
			</Box>
		);
	}

	renderLoadingSkeleton = () => (
		<Box>
			<Grid container spacing={1} sx={{ mb: 2 }}>
				{[1, 2, 3].map(i => (
					<Grid item xs={4} key={i}>
						<Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, textAlign: 'center' }}>
							<Skeleton variant="text" width="50%" sx={{ mx: 'auto', fontSize: 24 }} />
							<Skeleton variant="text" width="60%" sx={{ mx: 'auto', fontSize: 12 }} />
						</Paper>
					</Grid>
				))}
			</Grid>
			<Skeleton variant="rounded" height={80} sx={{ borderRadius: 2, mb: 2 }} />
			<Skeleton variant="rounded" height={80} sx={{ borderRadius: 2, mb: 2 }} />
			<Skeleton variant="rounded" height={80} sx={{ borderRadius: 2, mb: 2 }} />
			<Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
		</Box>
	)

	render() {
		const {statistics} = this.state;

		return (
			<Card variant="outlined" sx={{ borderRadius: 2.5 }}>
				<CardActionArea onClick={this.triggerCollapse}>
					<CardHeader
						sx={{ py: 1.25 }}
						avatar={<BarChartIcon sx={{ color: 'text.secondary', fontSize: 22 }} />}
						title={
							<Typography variant="h5" sx={{ fontSize: 21 }}>
								Workout Statistics
							</Typography>
						}
						action={
							<IconButton size="small">
								{this.state.isCollapseOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
							</IconButton>
						}
					/>
				</CardActionArea>
				<Collapse in={this.state.isLoading}>
					<LinearProgress color="secondary" />
				</Collapse>

				<Collapse in={this.state.isCollapseOpen}>
					<CardContent sx={{ pt: 1 }}>
						{this.hasNeverBeenFetched()
							? this.renderLoadingSkeleton()
							: this.renderCardContent(statistics)
						}
					</CardContent>
				</Collapse>
			</Card>
		);
	}
}

export default withRouter(GeneralStatsCard);
